import { PriceStatus } from '@toggle/helpers';
import { Entity, mapEntity, PriceHistory } from '@toggle/toggle';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NEW_YORK_TIMEZONE } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { useFlags } from '~/stores/use-flags/useFlags';

import styles from './CatalystSingleMarketReactionPrice.module.scss';
import { formatPrice, priceStatusClassMap } from './utils';
import { formatProportionChange } from '../utils';

export interface CatalystSingleMarketReactionPriceProps {
  entity: Entity;
  isWindowComplete: boolean;
  eventProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export const CatalystSingleMarketReactionPrice = ({
  entity,
  isWindowComplete,
  eventProportionChange,
  prevCloseData,
  lastKnownPrice,
  lastKnownDate,
}: CatalystSingleMarketReactionPriceProps) => {
  const { t } = useTranslation(['insights']);
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );

  if (eventProportionChange === undefined) {
    return null;
  }

  const mappedEntity = mapEntity(entity);
  const formattedPrice = lastKnownPrice
    ? formatPrice(lastKnownPrice, mappedEntity)
    : undefined;

  const getDateLabel = () => {
    if (isWindowComplete) {
      const windowCloseDate = lastKnownDate ?? prevCloseData?.time;
      if (!windowCloseDate) return null;

      return (
        <span
          className={clsx(styles.dateSpan, {
            [styles.dateSpanV2]: isUniversalDrawerEnabled,
          })}
        >
          {t('insights:catalyst.drawer.asOfDateClose', {
            date: formatInTimeZone(new Date(windowCloseDate), 'UTC', 'MMM d'),
          })}
        </span>
      );
    }

    if (!lastKnownDate) return null;

    return (
      <span
        className={clsx(styles.dateSpan, {
          [styles.dateSpanV2]: isUniversalDrawerEnabled,
        })}
      >
        {t('insights:catalyst.drawer.asOfDate', {
          date: `${formatInTimeZone(
            new Date(lastKnownDate),
            NEW_YORK_TIMEZONE,
            'MMM d, h:mm a'
          )} ET`,
        })}
      </span>
    );
  };

  return (
    <div
      data-testid="catalyst-market-reaction-price"
      className={styles.wrapper}
    >
      {getDateLabel()}
      <div
        className={clsx(styles.proportionChangeContainer, {
          [styles.proportionChangeContainerV2]: isUniversalDrawerEnabled,
        })}
      >
        {formattedPrice !== undefined && (
          <>
            <div className={styles.priceRow}>
              <span className={styles.proportionPrice}>{formattedPrice}</span>
              <span className={styles.proportionCurrency}>
                {mappedEntity.currency}
              </span>
            </div>
            <span
              className={clsx(
                styles.percentageChange,
                priceStatusClassMap[
                  eventProportionChange > 0
                    ? PriceStatus.Positive
                    : eventProportionChange < 0
                    ? PriceStatus.Negative
                    : PriceStatus.Default
                ]
              )}
            >
              {formatProportionChange(eventProportionChange)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
